// frontend/src/pages/NotificationsPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of CorrespondencesPage, ResponsesPage, and ProjectsPage
// FIXED: TypeScript errors for notification.read and params.read

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks following the pattern
import {
  useNotifications,
  useNotification,
  useUnreadNotificationCount,
  useNotificationSettings,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  useDismissNotification,
  useBatchMarkNotificationsRead,
  useBatchDeleteNotifications,
  useBatchNotificationOperations
} from '@/hooks/useNotifications';

// FIXED: Import useApi and useMutation for custom operations
import {
  useApi,
  useMutation
} from '@/hooks/useApi';

// FIXED: Import API types
import type {
  Notification,
  NotificationQueryParams,
  NotificationType,
  NotificationCategory,
  Priority,
  UserRole
} from '@/api/types';

import apiClient from '@/api';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class NotificationsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('NotificationsPage Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('NotificationsPage Error:', error, errorInfo);
    
    // Report error to app store for global error handling
    const { setGlobalError } = useAppStore.getState();
    setGlobalError(`Notifications Page Error: ${error.message}`);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== INTERFACES =====

interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
  todayCount: number;
  weekCount: number;
  monthCount: number;
}

interface BatchOperationRequest {
  action: 'markRead' | 'markUnread' | 'delete' | 'dismiss';
  notificationIds: string[];
  reason?: string;
}

// ===== NOTIFICATION CARD COMPONENT =====
const NotificationCard = memo<{
  notification: Notification;
  onMarkRead: (id: string) => void;
  onDismiss: (id: string, reason?: string) => void;
  onDelete: (id: string) => void;
}>(({ notification, onMarkRead, onDismiss, onDelete }) => {
  
  const getTypeIcon = useCallback((type: string): string => {
    switch (type) {
      case 'CORRESPONDENCE_CREATED': return '📧';
      case 'RESPONSE_ASSIGNED': return '📝';
      case 'RESPONSE_APPROVED': return '✅';
      case 'RESPONSE_REJECTED': return '❌';
      case 'WORKFLOW_STAGE_CHANGED': return '🔄';
      case 'GROUP_MEMBERSHIP_CHANGED': return '👥';
      case 'PROJECT_ASSIGNED': return '📂';
      case 'DOCUMENT_UPLOADED': return '📎';
      case 'SYSTEM_ALERT': return '⚠️';
      default: return '🔔';
    }
  }, []);

  const getPriorityColor = useCallback((priority?: string): string => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300 border-gray-200';
    }
  }, []);

  const getCategoryColor = useCallback((category?: string): string => {
    switch (category) {
      case 'system': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'workflow': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'correspondence': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'project': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  }, []);

  const notificationId = (notification as any)._id || notification.id || '';
  // FIXED: Use isRead instead of read
  const isUnread = !notification.isRead;

  // Event handlers
  const handleMarkRead = useCallback(() => onMarkRead(notificationId), [onMarkRead, notificationId]);
  const handleDismiss = useCallback(() => onDismiss(notificationId), [onDismiss, notificationId]);
  const handleDelete = useCallback(() => onDelete(notificationId), [onDelete, notificationId]);

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-4 transition-all duration-200 hover:shadow-md',
      isUnread 
        ? 'border-l-4 border-l-blue-500 border-gray-200 dark:border-gray-700'
        : 'border-gray-200 dark:border-gray-700'
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="text-2xl">
            {getTypeIcon(notification.type || 'SYSTEM_ALERT')}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className={cn(
                'text-sm font-medium',
                isUnread 
                  ? 'text-gray-900 dark:text-white' 
                  : 'text-gray-700 dark:text-gray-300'
              )}>
                {notification.title}
              </h3>
              {isUnread && (
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {notification.priority && (
                <span className={cn(
                  'px-2 py-1 text-xs font-medium rounded-full',
                  getPriorityColor(notification.priority)
                )}>
                  {notification.priority}
                </span>
              )}
              {notification.category && (
                <span className={cn(
                  'px-2 py-1 text-xs font-medium rounded-full',
                  getCategoryColor(notification.category)
                )}>
                  {notification.category}
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(notification.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Menu */}
        <div className="flex items-center space-x-1 ml-4">
          {isUnread && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkRead}
              className="text-xs"
              title="Mark as read"
            >
              ✓
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismiss}
            className="text-xs"
            title="Dismiss"
          >
            ∖
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            title="Delete"
          >
            🗑
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={cn(
        'text-sm mb-3',
        isUnread 
          ? 'text-gray-800 dark:text-gray-200' 
          : 'text-gray-600 dark:text-gray-400'
      )}>
        {notification.message && truncateText(notification.message, 200)}
      </div>

      {/* Metadata */}
      {(notification.relatedEntityId || notification.actionUrl) && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            {notification.relatedEntityId && (
              <span>ID: {truncateText(notification.relatedEntityId, 20)}</span>
            )}
          </div>
          {notification.actionUrl && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(notification.actionUrl, '_blank')}
              className="text-xs"
            >
              View Details
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

NotificationCard.displayName = 'NotificationCard';

// ===== MAIN NOTIFICATIONS PAGE COMPONENT =====
export const NotificationsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'read' | 'unread'>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Query params following pattern from other pages
  const queryParams = useMemo((): NotificationQueryParams => {
    const params: NotificationQueryParams = {
      page: currentPage,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    
    if (debouncedSearchTerm?.trim()) {
      params.search = debouncedSearchTerm.trim();
    }
    
    if (typeFilter && typeFilter !== 'all') {
      params.type = typeFilter as NotificationType;
    }
    
    if (priorityFilter && priorityFilter !== 'all') {
      params.priority = priorityFilter as Priority;
    }
    
    // FIXED: Use isRead instead of read
    if (readFilter !== 'all') {
      params.isRead = readFilter === 'read';
    }
    
    return params;
  }, [debouncedSearchTerm, typeFilter, priorityFilter, readFilter, currentPage]);

  // Load notifications using new API pattern
  const { 
    data: notificationsResponse, 
    loading: notificationsLoading, 
    error: notificationsError,
    refetch: refreshNotifications
  } = useNotifications(queryParams);

  // Load unread count
  const { data: unreadCountResponse } = useUnreadNotificationCount();

  // Load notification stats
  const { data: statsResponse } = useApi<NotificationStats>(
    async () => {
      // Try to get stats from notifications API
      if (apiClient.notifications && 'getStats' in apiClient.notifications) {
        return await (apiClient.notifications as any).getStats();
      } else if (apiClient.notifications && 'getSummary' in apiClient.notifications) {
        // Fallback to summary endpoint
        const summary = await (apiClient.notifications as any).getSummary();
        return {
          total: summary.totalCount || 0,
          unread: summary.unreadCount || 0,
          read: (summary.totalCount || 0) - (summary.unreadCount || 0),
          byType: summary.typeBreakdown || {},
          byPriority: summary.priorityBreakdown || {},
          byCategory: summary.categoryBreakdown || {},
          todayCount: 0,
          weekCount: 0,
          monthCount: 0
        };
      } else {
        return {
          total: 0,
          unread: 0,
          read: 0,
          byType: {},
          byPriority: {},
          byCategory: {},
          todayCount: 0,
          weekCount: 0,
          monthCount: 0
        };
      }
    },
    { 
      immediate: true,
      rateLimitKey: 'notification_stats'
    }
  );

  // Error handling
  useEffect(() => {
    if (notificationsError) {
      setGlobalError(`Notifications loading error: ${notificationsError}`);
    } else {
      clearGlobalError();
    }
  }, [notificationsError, setGlobalError, clearGlobalError]);

  // Auto-refresh notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshNotifications]);

  // Mutation hooks
  const markReadMutation = useMarkNotificationRead({
    onSuccess: () => {
      refreshNotifications();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to mark as read: ${error.message || error}`);
    }
  });

  const markAllReadMutation = useMarkAllNotificationsRead({
    onSuccess: () => {
      refreshNotifications();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to mark all as read: ${error.message || error}`);
    }
  });

  const dismissMutation = useDismissNotification({
    onSuccess: () => {
      refreshNotifications();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to dismiss notification: ${error.message || error}`);
    }
  });

  const deleteNotificationMutation = useDeleteNotification({
    onSuccess: () => {
      refreshNotifications();
      setSelectedNotifications(prev => prev.filter(id => id !== prev[0])); // Remove the deleted one
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to delete notification: ${error.message || error}`);
    }
  });

  const batchOperationMutation = useBatchNotificationOperations({
    onSuccess: () => {
      refreshNotifications();
      setSelectedNotifications([]);
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Batch operation failed: ${error.message || error}`);
    }
  });

  // Extract data from responses
  const notifications = notificationsResponse?.data || [];
  const pagination = notificationsResponse?.pagination;
  const unreadCount = unreadCountResponse?.count || 0;
  const stats: NotificationStats = statsResponse || {
    total: 0,
    unread: 0,
    read: 0,
    byType: {},
    byPriority: {},
    byCategory: {},
    todayCount: 0,
    weekCount: 0,
    monthCount: 0
  };

  // Event handlers
  const handleMarkRead = useCallback(async (id: string) => {
    try {
      await markReadMutation.mutate(id);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [markReadMutation]);

  const handleMarkAllRead = useCallback(async () => {
    if (stats.unread === 0) {
      setGlobalError('No unread notifications to mark');
      return;
    }

    const confirmed = await confirm({
      title: 'Mark All Read',
      message: `Are you sure you want to mark all ${stats.unread} unread notifications as read?`,
      confirmText: 'Mark All Read',
      variant: 'info',
    });
    if (confirmed) {
      try {
        await markAllReadMutation.mutate(undefined);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [markAllReadMutation, stats.unread, setGlobalError, confirm]);

  const handleDismiss = useCallback(async (id: string, reason?: string) => {
    try {
      await dismissMutation.mutate({ id, reason });
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [dismissMutation]);

  const handleDelete = useCallback(async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Notification',
      message: 'Are you sure you want to delete this notification? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteNotificationMutation.mutate(id);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [deleteNotificationMutation, confirm]);

  const handleBatchAction = useCallback(async (action: 'markRead' | 'markUnread' | 'delete' | 'dismiss', reason?: string) => {
    if (selectedNotifications.length === 0) {
      setGlobalError('No notifications selected');
      return;
    }
    
    const confirmMessage = action === 'delete' 
      ? `Are you sure you want to delete ${selectedNotifications.length} notifications? This action cannot be undone.`
      : `Are you sure you want to ${action} ${selectedNotifications.length} notifications?`;
    
    const confirmed = await confirm({
      title: action === 'delete' ? 'Delete Notifications' : `Batch ${action}`,
      message: confirmMessage,
      confirmText: action === 'delete' ? 'Delete' : 'Confirm',
      variant: action === 'delete' ? 'danger' : 'info',
    });
    if (confirmed) {
      try {
        await batchOperationMutation.mutate({
          action,
          notificationIds: selectedNotifications,
          reason
        });
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [selectedNotifications, batchOperationMutation, setGlobalError]);

  const handleSelectAll = useCallback(() => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(n => (n as any)._id || n.id || '').filter(Boolean));
    }
  }, [selectedNotifications.length, notifications]);

  const handleSelectNotification = useCallback((id: string) => {
    setSelectedNotifications(prev => 
      prev.includes(id) 
        ? prev.filter(nId => nId !== id)
        : [...prev, id]
    );
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedNotifications([]); // Clear selection when changing pages
  }, []);

  // Page actions
  const actions = useMemo(() => {
    const actionButtons = [];

    if (stats.unread > 0) {
      actionButtons.push(
        <Button
          key="mark-all-read"
          onClick={handleMarkAllRead}
          variant="outline"
          disabled={markAllReadMutation.loading}
        >
          {markAllReadMutation.loading ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" />
              <span>Marking...</span>
            </div>
          ) : (
            `Mark All Read (${stats.unread})`
          )}
        </Button>
      );
    }

    if (selectedNotifications.length > 0) {
      actionButtons.push(
        <div key="batch-actions" className="flex items-center space-x-2">
          <Button
            onClick={() => handleBatchAction('markRead')}
            variant="outline"
            size="sm"
            disabled={batchOperationMutation.loading}
          >
            Mark Read ({selectedNotifications.length})
          </Button>
          <Button
            onClick={() => handleBatchAction('dismiss')}
            variant="outline"
            size="sm"
            disabled={batchOperationMutation.loading}
          >
            Dismiss ({selectedNotifications.length})
          </Button>
          <Button
            onClick={() => handleBatchAction('delete')}
            variant="outline"
            size="sm"
            className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
            disabled={batchOperationMutation.loading}
          >
            Delete ({selectedNotifications.length})
          </Button>
        </div>
      );
    }

    return actionButtons;
  }, [stats.unread, selectedNotifications.length, handleMarkAllRead, handleBatchAction, markAllReadMutation.loading, batchOperationMutation.loading]);

  // Permission check (all users should be able to see their notifications)
  const hasAccess = !!currentUser;

  if (!hasAccess) {
    return (
      <PageLayout title="Notifications" subtitle="Access Restricted">
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Authentication Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>Please log in to view your notifications.</p>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <NotificationsErrorBoundary>
      <PageLayout 
        title={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        subtitle="Stay updated with system alerts and activity"
        actions={actions}
        loading={notificationsLoading}
      >
        <div className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Notifications
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.unread}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Unread
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.read}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Read
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.todayCount}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Today
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Notifications
                </label>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search by title or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Type Filter */}
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="CORRESPONDENCE_CREATED">Correspondence</option>
                  <option value="RESPONSE_ASSIGNED">Response Assigned</option>
                  <option value="RESPONSE_APPROVED">Response Approved</option>
                  <option value="WORKFLOW_STAGE_CHANGED">Workflow</option>
                  <option value="SYSTEM_ALERT">System Alert</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label htmlFor="priority-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  id="priority-filter"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Read Status Filter */}
              <div>
                <label htmlFor="read-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id="read-filter"
                  value={readFilter}
                  onChange={(e) => setReadFilter(e.target.value as 'all' | 'read' | 'unread')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          {notificationsLoading ? (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading notifications...</p>
            </div>
          ) : notificationsError ? (
            <ErrorAlert 
              message={notificationsError}
              onClose={() => clearGlobalError()}
            />
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 00-15 0v5h5l-5 5-5-5h5V7a12.5 12.5 0 0125 0v10z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm || typeFilter !== 'all' || priorityFilter !== 'all' || readFilter !== 'all' 
                  ? 'No notifications found' 
                  : 'No notifications yet'
                }
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm || typeFilter !== 'all' || priorityFilter !== 'all' || readFilter !== 'all'
                  ? 'Try adjusting your search criteria.'
                  : 'You\'ll see notifications here when there\'s activity in the system.'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Selection Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedNotifications.length === notifications.length && notifications.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedNotifications.length > 0 
                          ? `${selectedNotifications.length} selected`
                          : 'Select all'
                        }
                      </span>
                    </label>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div className="space-y-4">
                {notifications.map((notification) => {
                  const notificationId = (notification as any)._id || notification.id || '';
                  return (
                    <div key={notificationId} className="relative">
                      <div className="absolute left-2 top-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedNotifications.includes(notificationId)}
                          onChange={() => handleSelectNotification(notificationId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="pl-8">
                        <NotificationCard
                          notification={notification}
                          onMarkRead={handleMarkRead}
                          onDismiss={handleDismiss}
                          onDelete={handleDelete}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                        variant="outline"
                        size="sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= pagination.totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Auto-refresh indicator */}
          <div className="fixed bottom-4 right-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Auto-refresh: 30s</span>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    </NotificationsErrorBoundary>
  );
};